import React from 'react';
import { Button, Badge } from 'components/ui';
import {
  UserIcon,
  CalendarDaysIcon,
  CalendarIcon,
  PencilSquareIcon,
  PaperClipIcon,
  TicketIcon,
  ChatBubbleLeftRightIcon,
  ChartBarIcon,
  CreditCardIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  VideoCameraIcon,
  ClockIcon,
  BanknotesIcon,
  Squares2X2Icon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import { hasPermission } from 'utils/permissionsUtils';

const SidebarNavigation = ({
  selectedTab,
  setSelectedTab,
  isMobile,
  showMobileSidebar,
  setShowMobileSidebar,
  selectedContentTab,
  setSelectedContentTab,
  entityName = "Lead",
  leadData = null,
  layout = "vertical"
}) => {
  const [expandedLeadDetails, setExpandedLeadDetails] = React.useState(selectedTab === 'leads-tab');
  const prevSelectedTab = React.useRef(selectedTab);

  // Refs for scroll containers
  const mainTabsRef = React.useRef(null);
  const subTabsRef = React.useRef(null);
  const mobileTabsRef = React.useRef(null);
  const mobileSubTabsRef = React.useRef(null);

  // Scroll visibility state
  const [showMainLeft, setShowMainLeft] = React.useState(false);
  const [showMainRight, setShowMainRight] = React.useState(false);
  const [showSubLeft, setShowSubLeft] = React.useState(false);
  const [showSubRight, setShowSubRight] = React.useState(false);
  const [showMobileLeft, setShowMobileLeft] = React.useState(false);
  const [showMobileRight, setShowMobileRight] = React.useState(false);
  const [showMobileSubLeft, setShowMobileSubLeft] = React.useState(false);
  const [showMobileSubRight, setShowMobileSubRight] = React.useState(false);

  // Function to check scroll overflow
  const checkScroll = React.useCallback((ref, setLeft, setRight) => {
    if (ref.current) {
      const { scrollLeft, scrollWidth, clientWidth } = ref.current;
      setLeft(scrollLeft > 5);
      setRight(scrollLeft + clientWidth < scrollWidth - 5);
    }
  }, []);

  // Set up observers for scroll and resize
  React.useEffect(() => {
    const mainRef = mainTabsRef;
    const subRef = subTabsRef;
    const mobileRef = mobileTabsRef;
    const mobileSubRef = mobileSubTabsRef;

    const handleMainScroll = () => checkScroll(mainRef, setShowMainLeft, setShowMainRight);
    const handleSubScroll = () => checkScroll(subRef, setShowSubLeft, setShowSubRight);
    const handleMobileScroll = () => checkScroll(mobileRef, setShowMobileLeft, setShowMobileRight);
    const handleMobileSubScroll = () => checkScroll(mobileSubRef, setShowMobileSubLeft, setShowMobileSubRight);

    if (mainRef.current) {
      mainRef.current.addEventListener('scroll', handleMainScroll);
      checkScroll(mainRef, setShowMainLeft, setShowMainRight);
    }
    if (subRef.current) {
      subRef.current.addEventListener('scroll', handleSubScroll);
      checkScroll(subRef, setShowSubLeft, setShowSubRight);
    }
    if (mobileRef.current) {
      mobileRef.current.addEventListener('scroll', handleMobileScroll);
      checkScroll(mobileRef, setShowMobileLeft, setShowMobileRight);
    }
    if (mobileSubRef.current) {
      mobileSubRef.current.addEventListener('scroll', handleMobileSubScroll);
      checkScroll(mobileSubRef, setShowMobileSubLeft, setShowMobileSubRight);
    }

    const handleWindowResize = () => {
      checkScroll(mainRef, setShowMainLeft, setShowMainRight);
      checkScroll(subRef, setShowSubLeft, setShowSubRight);
      checkScroll(mobileRef, setShowMobileLeft, setShowMobileRight);
      checkScroll(mobileSubRef, setShowMobileSubLeft, setShowMobileSubRight);
    };
    window.addEventListener('resize', handleWindowResize);

    const timeoutId = setTimeout(handleWindowResize, 500);

    return () => {
      if (mainRef.current) mainRef.current.removeEventListener('scroll', handleMainScroll);
      if (subRef.current) subRef.current.removeEventListener('scroll', handleSubScroll);
      if (mobileRef.current) mobileRef.current.removeEventListener('scroll', handleMobileScroll);
      if (mobileSubRef.current) mobileSubRef.current.removeEventListener('scroll', handleMobileSubScroll);
      window.removeEventListener('resize', handleWindowResize);
      clearTimeout(timeoutId);
    };
  }, [layout, selectedTab, checkScroll, isMobile]);

  // Scroll handler
  const scroll = (ref, direction) => {
    if (ref.current) {
      ref.current.scrollBy({ left: direction, behavior: 'smooth' });
    }
  };

  // Auto-scroll selected tab into view
  React.useEffect(() => {
    if (layout === 'horizontal' && !isMobile) {
      const activeBtn = mainTabsRef.current?.querySelector('[data-active="true"]');
      if (activeBtn) {
        // Delay slightly to allow for layouts to settle
        const timeoutId = setTimeout(() => {
          activeBtn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }, 100);
        return () => clearTimeout(timeoutId);
      }
    }
  }, [selectedTab, layout, isMobile]);

  React.useEffect(() => {
    if (layout === 'horizontal' && !isMobile) {
      const activeBtn = subTabsRef.current?.querySelector('[data-active="true"]');
      if (activeBtn) {
        const timeoutId = setTimeout(() => {
          activeBtn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }, 100);
        return () => clearTimeout(timeoutId);
      }
    }
  }, [selectedContentTab, layout, isMobile]);


  // Auto-expand lead details only when switching TO leads-tab from another tab
  React.useEffect(() => {
    // Only expand when switching from a different tab to leads-tab
    if (selectedTab === 'leads-tab' && prevSelectedTab.current !== 'leads-tab') {
      setExpandedLeadDetails(true);
    }
    prevSelectedTab.current = selectedTab;

    // Check scroll after tab switch
    setTimeout(() => {
      checkScroll(mainTabsRef, setShowMainLeft, setShowMainRight);
      checkScroll(subTabsRef, setShowSubLeft, setShowSubRight);
    }, 100);
  }, [selectedTab, checkScroll]);

  const tabs = React.useMemo(() => {
    const baseTabs = [
      { id: 'timeline', name: 'Timeline', icon: <CalendarDaysIcon className="w-5 h-5" />, permission: "MLLV" },
      { id: 'notes', name: 'Notes', icon: <PencilSquareIcon className="w-5 h-5" />, permission: "MLLV" },
      { id: 'lead-management', name: 'Management', icon: <Squares2X2Icon className="w-5 h-5" />, permission: "MLLV" },
      { id: 'application-details', name: 'Application Details', icon: <DocumentTextIcon className="w-5 h-5" />, permission: "MLLV" },
      { id: 'payment-details', name: 'Payment', icon: <CreditCardIcon className="w-5 h-5" />, permission: "MLLV" },
      { id: 'follow-up-calendar', name: 'Follow Up Calendar', icon: <ClockIcon className="w-5 h-5" />, permission: "MLLV" },
      { id: 'communications', name: 'Communication', icon: <ChatBubbleLeftRightIcon className="w-5 h-5" />, permission: "MLLV" },
    ];

    // Add Offer Letter for international leads
    if (leadData && leadData.IsInternationalLead) {
      baseTabs.splice(4, 0, { id: 'offer-letter', name: 'Offer Letter', icon: <DocumentTextIcon className="w-5 h-5" />, permission: "MLLV" });
    }

    return baseTabs;
  }, [leadData]);




  const handleMainTabClick = (tabId) => {
    setSelectedTab(tabId);
    if (isMobile) {
      setShowMobileSidebar(false);
    }
  };

  if (isMobile) {
    return (
      <>
        {/* Mobile Navigation */}
        {showMobileSidebar && (
          <div className="fixed inset-0 z-40 bg-gray-900/50 bg-opacity-50" onClick={() => setShowMobileSidebar(false)} />
        )}
        <div className={`
          ${showMobileSidebar ? 'translate-x-0' : '-translate-x-full'}
          fixed left-0 top-0 z-50 h-full w-64 bg-white dark:bg-dark-800 shadow-lg transition-transform duration-300 ease-in-out
          md:relative md:translate-x-0 md:shadow-none md:z-auto
        `}>
          <div className="p-4 border-b border-gray-200 dark:border-dark-600">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold text-gray-500 dark:text-dark-400 uppercase tracking-wider">
                Management
              </h3>
              <Badge variant="soft" color="info" className="text-xs">
                {tabs.length}
              </Badge>
            </div>
          </div>
          <div className="p-4 overflow-y-auto h-full">
            <div className="space-y-1">
              {tabs.map((tab) => {
                if (!hasPermission(tab.permission)) {
                  return null;
                }



                return (
                  <Button
                    key={tab.id}
                    onClick={() => handleMainTabClick(tab.id)}
                    variant={selectedTab === tab.id ? 'soft' : 'flat'}
                    color={selectedTab === tab.id ? 'primary' : 'neutral'}
                    className={`flex items-center gap-3 w-full justify-start px-3 py-3 text-sm transition-all duration-200 ${selectedTab === tab.id
                      ? 'shadow-sm'
                      : 'hover:translate-x-1'
                      }`}
                    unstyled={false}
                  >
                    <span className="w-5 h-5 shrink-0">{tab.icon}</span>
                    <div className="text-left">
                      <div className="font-medium">{tab.name}</div>
                      {tab.subtitle && (
                        <div className="text-xs opacity-75">
                          {tab.subtitle}
                        </div>
                      )}
                    </div>
                  </Button>
                )
              })}
            </div>
          </div>
        </div>

        {/* Mobile Tab Bar at Bottom - Now Scrollable with Arrows */}
        <div className="border-b border-gray-200 dark:border-dark-600 bg-gray-50/50 dark:bg-dark-800/50 relative group/mobile-tabs">
          {/* Left Scroll Button */}
          {showMobileLeft && (
            <button
              onClick={() => scroll(mobileTabsRef, -150)}
              className="absolute left-0 top-0 bottom-0 z-30 px-1 bg-gradient-to-r from-gray-50 dark:from-dark-800 to-transparent flex items-center justify-center text-gray-400 hover:text-primary-600 transition-opacity"
            >
              <ChevronLeftIcon className="w-5 h-5" />
            </button>
          )}

          <div
            ref={mobileTabsRef}
            className="overflow-x-auto hide-scrollbar scroll-smooth"
          >
            <div className="flex">
              {tabs.map((tab) => (
                <Button
                  key={tab.id}
                  onClick={() => handleMainTabClick(tab.id)}
                  variant="flat"
                  color={selectedTab === tab.id ? 'primary' : 'neutral'}
                  className={`flex flex-col items-center justify-center gap-0.5 px-3 py-2 border-b-2 transition-all duration-200 whitespace-nowrap ${selectedTab === tab.id
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 font-semibold flex-initial px-4'
                    : 'border-transparent text-gray-500 dark:text-dark-300 hover:text-gray-700 dark:hover:text-dark-200 flex-1'
                    }`}
                  unstyled={true}
                >
                  <span className="w-4 h-4 shrink-0">{React.cloneElement(tab.icon, { className: 'w-4 h-4' })}</span>
                  {selectedTab === tab.id && (
                    <span className="truncate text-[10px] leading-tight mt-0.5">
                      {tab.name.split(' ')[0]}
                    </span>
                  )}
                </Button>
              ))}
            </div>
          </div>

          {/* Right Scroll Button */}
          {showMobileRight && (
            <button
              onClick={() => scroll(mobileTabsRef, 150)}
              className="absolute right-0 top-0 bottom-0 z-30 px-1 bg-gradient-to-l from-gray-50 dark:from-dark-800 to-transparent flex items-center justify-center text-gray-400 hover:text-primary-600 transition-opacity"
            >
              <ChevronRightIcon className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Mobile Sub-tabs Row (Conditional) */}
        {selectedTab === 'leads-tab' && (
          <div className="border-b border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-900 relative group/mobile-subtabs">
            {/* Left Scroll Button */}
            {showMobileSubLeft && (
              <button
                onClick={() => scroll(mobileSubTabsRef, -120)}
                className="absolute left-0 top-0 bottom-0 z-30 px-1 bg-gradient-to-r from-white dark:from-dark-900 to-transparent flex items-center justify-center text-gray-400 hover:text-primary-600 transition-opacity"
              >
                <ChevronLeftIcon className="w-4 h-4" />
              </button>
            )}

            <div
              ref={mobileSubTabsRef}
              className="overflow-x-auto hide-scrollbar scroll-smooth"
            >
              <div className="flex px-2 py-2 gap-2">
                {leadDetailsSubTabs.map((subTab) => (
                  <Button
                    key={subTab.id}
                    onClick={() => handleSubTabClick(subTab.id)}
                    variant={selectedContentTab === subTab.id ? 'soft' : 'flat'}
                    color={selectedContentTab === subTab.id ? 'primary' : 'neutral'}
                    className={`px-3 py-1.5 text-[11px] font-bold rounded-lg transition-all duration-200 whitespace-nowrap border ${selectedContentTab === subTab.id
                      ? 'border-primary-200 dark:border-primary-900/30 shadow-sm'
                      : 'border-transparent text-gray-500 dark:text-dark-300'
                      }`}
                    unstyled={true}
                  >
                    <span className="flex items-center gap-1.5 uppercase tracking-wider">
                      {React.cloneElement(subTab.icon, { className: 'w-3.5 h-3.5' })}
                      {subTab.name}
                    </span>
                  </Button>
                ))}
              </div>
            </div>

            {/* Right Scroll Button */}
            {showMobileSubRight && (
              <button
                onClick={() => scroll(mobileSubTabsRef, 120)}
                className="absolute right-0 top-0 bottom-0 z-30 px-1 bg-gradient-to-l from-white dark:from-dark-900 to-transparent flex items-center justify-center text-gray-400 hover:text-primary-600 transition-opacity"
              >
                <ChevronRightIcon className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
      </>
    );
  }

  // --- Horizontal Desktop Navigation ---
  if (layout === 'horizontal' && !isMobile) {
    return (
      <div className="w-full bg-white dark:bg-dark-900 border-b border-gray-100 dark:border-dark-700 z-20">
        <div className="px-6">
          {/* Top Row: Title and Main Tabs */}
          <div className="relative group/tabs flex items-center overflow-hidden">
            {/* Left Edge Fade & Hidden Scroll */}
            <div className={`absolute left-0 top-0 bottom-0 w-16 z-30 pointer-events-none transition-opacity duration-300 bg-gradient-to-r from-white dark:from-dark-900 to-transparent ${showMainLeft ? 'opacity-100' : 'opacity-0'}`}>
              <button
                onClick={() => scroll(mainTabsRef, -200)}
                className="absolute left-0 top-1/2 -translate-y-1/2 p-2 pointer-events-auto opacity-0 group-hover/tabs:opacity-100 transition-opacity text-gray-400 hover:text-primary-600 dark:text-dark-400"
              >
                <ChevronLeftIcon className="w-5 h-5" />
              </button>
            </div>

            <nav
              ref={mainTabsRef}
              className="flex items-center gap-8 overflow-x-auto hide-scrollbar scroll-smooth py-1 w-full"
            >
              {tabs.map((tab) => {
                const isActive = selectedTab === tab.id;

                return (
                  <button
                    key={tab.id}
                    onClick={() => handleMainTabClick(tab.id)}
                    data-active={isActive}
                    className={`
                      relative py-5 text-[14px] font-bold transition-all duration-200 whitespace-nowrap
                      ${isActive
                        ? 'text-primary-600 dark:text-primary-400'
                        : 'text-gray-500 hover:text-gray-900 dark:text-dark-200 dark:hover:text-dark-100'
                      }
                    `}
                  >
                    <span className="tracking-wide">{tab.name}</span>

                    {/* Simplified Underline like SS 2 */}
                    {isActive ? (
                      <div className="absolute bottom-[-1px] left-0 right-0 h-[3px] bg-primary-600 dark:bg-primary-500 rounded-t-full shadow-[0_-2px_8px_rgba(37,99,235,0.4)]"></div>
                    ) : (
                      <div className="absolute bottom-[-1px] left-0 right-0 h-[2px] bg-transparent transition-all duration-200 group-hover:bg-gray-200 dark:group-hover:bg-dark-700"></div>
                    )}
                  </button>
                );
              })}
            </nav>

            {/* Right Edge Fade & Hidden Scroll */}
            <div className={`absolute right-0 top-0 bottom-0 w-16 z-30 pointer-events-none transition-opacity duration-300 bg-gradient-to-l from-white dark:from-dark-900 to-transparent ${showMainRight ? 'opacity-100' : 'opacity-0'}`}>
              <button
                onClick={() => scroll(mainTabsRef, 200)}
                className="absolute right-0 top-1/2 -translate-y-1/2 p-2 pointer-events-auto opacity-0 group-hover/tabs:opacity-100 transition-opacity text-gray-400 hover:text-primary-600 dark:text-dark-400"
              >
                <ChevronRightIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Bottom Row: Sub-tabs (Conditional) - also simplified */}

      </div>
    );
  }

  // Desktop Navigation - Professional Design (Vertical)
  return (
    <div className="w-64 border-r border-gray-200/60 dark:border-dark-600/60 bg-white dark:bg-dark-800 shrink-0">
      <div className="p-5">
        {/* Professional Header */}
        <div className="mb-6 pb-4 border-b border-gray-200 dark:border-dark-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-1 h-7 bg-gradient-to-b from-primary-600 to-primary-700 rounded-full"></div>
              <h3 className="text-[11px] font-bold text-gray-800 dark:text-dark-100 uppercase tracking-[0.08em]">
                Management
              </h3>
            </div>
            <Badge
              variant="soft"
              color="info"
              className="text-[10px] font-semibold px-2 py-0.5"
            >
              {tabs.length}
            </Badge>
          </div>
        </div>

        <nav className="space-y-1">
          {tabs.map((tab) => {


            return (
              <Button
                key={tab.id}
                onClick={() => handleMainTabClick(tab.id)}
                variant="flat"
                color="neutral"
                className={`
                  group relative flex items-center gap-3 w-full justify-start px-3 py-2.5 text-sm 
                  transition-all duration-200 rounded-lg
                  ${selectedTab === tab.id
                    ? 'bg-primary-50 dark:bg-primary-950/20 text-primary-700 dark:text-primary-400 shadow-sm'
                    : 'text-gray-700 dark:text-dark-300 hover:bg-gray-50 dark:hover:bg-dark-700/50'
                  }
                `}
                unstyled={true}
              >
                {/* Icon */}
                <div className={`
                  w-8 h-8 rounded-lg flex items-center justify-center shrink-0
                  transition-all duration-200
                  ${selectedTab === tab.id
                    ? 'bg-gradient-to-br from-primary-500 to-primary-600 text-white shadow-md shadow-primary-500/25'
                    : 'bg-gray-100 dark:bg-dark-700 text-gray-500 dark:text-dark-400 group-hover:bg-primary-50 group-hover:text-primary-600 dark:group-hover:bg-primary-950/30 dark:group-hover:text-primary-400'
                  }
                `}>
                  {React.cloneElement(tab.icon, { className: 'w-4 h-4', strokeWidth: 2 })}
                </div>

                <div className="text-left">
                  <div className={`font-semibold text-[13px] leading-tight ${selectedTab === tab.id
                    ? 'text-primary-700 dark:text-primary-400'
                    : 'text-gray-700 dark:text-dark-200 group-hover:text-gray-900 dark:group-hover:text-dark-100'
                    }`}>
                    {tab.name}
                  </div>
                  {tab.subtitle && (
                    <div className={`text-[11px] mt-0.5 ${selectedTab === tab.id
                      ? 'text-primary-600/70 dark:text-primary-400/70'
                      : 'text-gray-500 dark:text-dark-400'
                      }`}>
                      {tab.subtitle}
                    </div>
                  )}
                </div>
              </Button>
            );
          })}
        </nav>
      </div>
    </div>
  );
};

export default SidebarNavigation;
